import { useStoryAnalytics } from '@/hooks/stories'
import { ExportButton } from '@/components/export/ExportButton'
import { AnalyticsKpiRail } from '@/components/analytics/AnalyticsKpiRail'
import { ConfidenceLadder } from '@/components/analytics/ConfidenceLadder'
import { SourceTierMix } from '@/components/analytics/SourceTierMix'
import { SingleSourceRail } from '@/components/analytics/SingleSourceRail'
import { StoriesPerDay } from '@/components/analytics/StoriesPerDay'
import { GeographyTopN } from '@/components/analytics/GeographyTopN'
import { IndustryConfidenceHeatmap } from '@/components/analytics/IndustryConfidenceHeatmap'
import { AttentionMix } from '@/components/analytics/AttentionMix'
import type { TimeRange } from '@/types/story'

const RANGES: TimeRange[] = ['7d', '30d', '90d']

export interface StoriesAnalyticsPageProps {
  range: TimeRange
  setRange: (r: TimeRange) => void
}

/**
 * SP4 PR-2 — Story Coverage analytics surface.
 *
 * Renders the 7 chart components + KPI rail + threat-landscape mix widget. Time-range
 * state is owned by the parent shell (AnalyticsPageShell) and flows through as
 * a prop — the range selector still lives here so switching tabs preserves the
 * selection without visual regression.
 */
export function StoriesAnalyticsPage({ range, setRange }: StoriesAnalyticsPageProps) {
  const { data, isLoading, isError, refetch } = useStoryAnalytics(range)

  const totals = data?.totals ?? {
    total: 0,
    pctEstablished: 0,
    pctSingleSource: 0,
    avgClusterSize: 0,
    priorPeriod: {
      total: 0,
      pctEstablished: 0,
      pctSingleSource: 0,
      avgClusterSize: 0,
    },
  }
  const onRetry = () => {
    void refetch()
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Story Coverage</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            How broad and corroborated the IntelHub feed is — and how the landscape is moving.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            data-testid="sp4-range-selector"
            className="flex gap-1 rounded-lg border bg-muted p-1"
            role="group"
            aria-label="Time range"
          >
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                aria-pressed={range === r}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  range === r
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <ExportButton entity={{ type: 'Analytics', name: 'Story Coverage', extra: range }} />
        </div>
      </header>

      {/* Coverage & Trust */}
      <section aria-labelledby="coverage-trust-h" className="flex flex-col gap-4">
        <div>
          <h2 id="coverage-trust-h" className="text-sm font-semibold">Coverage &amp; Trust</h2>
          <p className="text-xs text-muted-foreground">How broad and corroborated the IntelHub feed is — feed trust, not threat volume.</p>
        </div>
        <AnalyticsKpiRail totals={totals} />
        <ConfidenceLadder data={data?.confidenceLadder ?? []} isLoading={isLoading} isError={isError} onRetry={onRetry} />
        <SingleSourceRail data={data?.singleSourceRisk ?? { current: 0, prior: 0, daily: [] }} isLoading={isLoading} isError={isError} onRetry={onRetry} />
      </section>

      {/* Threat landscape */}
      <section aria-labelledby="threat-landscape-h" className="flex flex-col gap-4">
        <div>
          <h2 id="threat-landscape-h" className="text-sm font-semibold">Threat landscape</h2>
          <p className="text-xs text-muted-foreground">Directional exposure and movement signals for the selected range.</p>
        </div>
        <div data-analytics-grid="true" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <GeographyTopN data={data?.geographyTopN ?? []} totalJurisdictions={(data?.geographyTopN ?? []).length} isLoading={isLoading} isError={isError} onRetry={onRetry} />
          <IndustryConfidenceHeatmap data={data?.industryConfidence ?? []} isLoading={isLoading} isError={isError} onRetry={onRetry} />
        </div>
        <AttentionMix data={data?.sectionTrend ?? []} windowLabel={range} isLoading={isLoading} isError={isError} onRetry={onRetry} />
      </section>

      {/* Content operations (demoted-in-place; ops home is KAN-231-E) */}
      <section aria-labelledby="content-ops-h" className="flex flex-col gap-4">
        <div>
          <h2 id="content-ops-h" className="text-sm font-semibold">Content operations</h2>
          <p className="text-xs text-muted-foreground">How the publishing pipeline is behaving — internal coverage diagnostics, not decision support.</p>
        </div>
        <div data-analytics-grid="true" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SourceTierMix data={data?.sourceTierMix ?? []} isLoading={isLoading} isError={isError} onRetry={onRetry} />
          <StoriesPerDay data={data?.storiesPerDay ?? []} isLoading={isLoading} isError={isError} onRetry={onRetry} />
        </div>
      </section>
    </div>
  )
}
